import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/buttons/button";
import { ProfileIcon } from "../../../components/cards/profilePic";
import { SearchInput } from "../../../components/inputs/searchInput";
import { ManageSalesAgentModal } from "../../../components/modal/app_modals/manangeSalesAgentModal";
import { TableComponent } from "../../../components/table";
import { MiniHeader } from "../../../components/typography/miniHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../customeHooks/useModalControl";
import { useGetAllSalesAgentsQuery } from "../../../redux/features/admin/sales/sales.api";
import { useExportDataMutation } from "../../../redux/features/misc/misc.api";
import { formatDate, formatToCurrency } from "../../../utils/helpers";
import { QueryParams } from "../../../utils/types";

export const SalesOverview = () => {
  const [query, setQuery] = useState<QueryParams>({
    page: 1,
    limit: 50,
    search: undefined,
  });

  const navigate = useNavigate();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const [exportData, { isLoading: exportingData }] = useExportDataMutation();

  const {
    isFetching: fetchingAgents,
    data: agentsData,
    refetch: refetchAgents,
  } = useGetAllSalesAgentsQuery(
    { ...query, search: query.search === "" ? undefined : query.search },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  return (
    <>
      <div className="flex flex-col gap-[2px]">
        <BgWrapper className="flex flex-row items-center justify-between gap-[10px]">
          <MiniHeader
            header={`${agentsData?.data?.totalCount ?? 0} Sales ${(agentsData?.data?.totalCount ?? 0) === 1 ? "Agent" : "Agents"}`}
          />

          <Button
            type="button"
            onClick={() => {
              handleModalOpen("createAgentModal");
            }}
            text="Add Sales Agent"
            className="!w-fit !p-0 !px-0 !py-0"
            icon="plus"
            variation="blue"
          />
        </BgWrapper>

        <BgWrapper className="flex flex-col gap-3">
          <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
            <SearchInput
              searchFunction={(value) => {
                setQuery({
                  ...query,
                  search: value,
                  page: 1,
                });
              }}
              placeholder="Search"
              className="md:!max-w-[300px]"
            />

            <div className="grid w-full grid-cols-2 items-center gap-3 md:flex md:w-fit md:flex-row">
              <Button
                type="button"
                onClick={() => {
                  if (query.sortOrder === "asc") {
                    setQuery({ ...query, sortOrder: "desc" });
                  } else {
                    setQuery({ ...query, sortOrder: "asc" });
                  }
                }}
                text="Date Modified"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon={query.sortOrder === "asc" ? "order-asc" : "order-desc"}
                variation="grey"
              />

              <Button
                type="button"
                onClick={async () => {
                  await exportData({
                    type: "users",
                    fetchAll: true,
                    sortOrder: query.sortOrder,
                    search: query.search || undefined,
                    searchTerm: query.search || undefined,
                  });
                }}
                text="Export"
                className="!min-w-fit !rounded-2xl !p-3 !text-sm"
                icon="export"
                variation="grey"
                loading={exportingData}
              />
            </div>
          </div>

          <TableComponent
            headers={[]}
            rows={
              agentsData?.data?.data?.map((agent, idx) => {
                return {
                  id: idx,
                  items: [
                    <div
                      className={`flex w-full flex-1 flex-col gap-2 md:flex-row md:items-center`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <ProfileIcon img="" dimension={32} className="" />
                      </div>

                      <p className="flex flex-1 flex-wrap items-center gap-0 text-sm text-grey-600">
                        <span className="font-bold text-grey-900">
                          {agent.firstName} {agent.lastName}
                        </span>
                        <span className={` `}>∙</span>
                        <span className={``}>
                          {formatToCurrency(agent.commission.NGN, {
                            currency: "NGN",
                          })}
                        </span>
                        <span className={` `}>∙</span>
                        <span className={``}>
                          {formatToCurrency(agent.commission.CAD, {
                            currency: "CAD",
                          })}
                        </span>
                        <span>&nbsp;per paid ticket</span>
                      </p>

                      <p className="flex flex-row flex-wrap items-center gap-2 text-grey-600">
                        Joined on {formatDate(agent.createdAt)}
                      </p>
                    </div>,
                  ],
                };
              }) ?? []
            }
            loadingRows={query.limit}
            isLoading={fetchingAgents}
            isRowClickable
            onRowClick={(idx) => {
              navigate(`/sales/agent/${agentsData?.data?.data[idx]?._id}`);
            }}
            isTableEmpty={[0, undefined].includes(
              agentsData?.data?.data?.length,
            )}
            emptyTitle="No Sales Agents"
            emptySubTitle=""
            emptyImage="/assets/images/guest-glass.png"
            isPaginated
            currentPage={query.page ?? 1}
            totalCount={agentsData?.data?.totalCount ?? 0}
            itemsPerPage={query.limit!}
            pageRangeDisplayed={3}
            onPageChange={(page) => {
              setQuery({
                ...query,
                page: page,
              });
            }}
            className=""
          />
        </BgWrapper>
      </div>

      <ManageSalesAgentModal
        open={modalOpen && modal === "createAgentModal"}
        handleClose={() => {
          handleModalClose();
        }}
        onSuccess={() => {
          refetchAgents();
        }}
      />
    </>
  );
};
